(function () {
    'use strict';

    var _flChatInited = false;

    // Public entry point. Safe to call multiple times and before the markup exists —
    // it no-ops until #chat-window is on the page, then wires everything up once.
    // The /chat page has the markup at load and auto-inits below; the Browse page
    // injects the panel on first use and calls window.initFLChat() itself.
    function initFLChat() {
    if (_flChatInited) return;
    if (!document.getElementById('chat-window')) return;
    _flChatInited = true;

    const API_BASE   = '/ai';
    const _csrfMeta  = document.querySelector('meta[name="csrf-token"]');
    const CSRF_TOKEN = _csrfMeta ? _csrfMeta.content : '';

    const chatWindow          = document.getElementById('chat-window');
    const userInput           = document.getElementById('user-input');
    const sendBtn             = document.getElementById('send-btn');
    const newChatBtn          = document.getElementById('new-chat-btn');
    const emptyState          = document.getElementById('empty-state');
    const statusPill          = document.getElementById('ai-status');
    const traceModal          = document.getElementById('trace-modal');
    const traceList           = document.getElementById('trace-list');
    const closeTraceBtn       = document.getElementById('close-trace');
    const graphTypeSelect     = document.getElementById('graph-type');
    const graphInAnswerCheck  = document.getElementById('graph-in-answer');
    const exportFormatSelect  = document.getElementById('export-format');
    const exportBtn           = document.getElementById('export-btn');

    let sessionId             = newSessionId();
    let isSending             = false;
    let latestExportPayload   = null;

    function newSessionId() {
        return 'session-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    }

    function getStoredTenantDb() {
        const meta = document.querySelector('meta[name="tenant"]');
        return meta ? meta.content : null;
    }

    // The real logged-in user (from layouts/master's meta tag) — sent so the AI backend can apply
    // this user's own field/table/record permission scope, not an unrestricted/anonymous one.
    function getCurrentUserId() {
        const meta = document.querySelector('meta[name="user_id"]');
        return meta ? meta.content : null;
    }

    // ── Status ────────────────────────────────────────────────────────
    function setStatus(online, text) {
        statusPill.style.borderColor  = online ? '#bbf7d0' : '#fecaca';
        statusPill.style.background   = online ? '#f0fdf4' : '#fef2f2';
        statusPill.style.color        = online ? '#15803d' : '#b91c1c';
        statusPill.innerHTML = '<span style="width:6px;height:6px;border-radius:50%;background:currentColor;display:inline-block"></span><span>' + text + '</span>';
    }

    async function checkHealth() {
        try {
            const res = await fetch(API_BASE + '/health');
            if (!res.ok) throw new Error();
            setStatus(true, 'Connected');
        } catch { setStatus(false, 'Offline'); }
    }

    // ── Markdown ──────────────────────────────────────────────────────
    function formatColumnHeader(raw) {
        return raw.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }

    function preProcessMarkdown(content) {
        if (!content) return '';
        content = content.replace(/\b(\d{1,3}(?:,\d{3})*|\d+)(\.\d{5,})\b/g, (match, intPart, decPart) => {
            const num = parseFloat(intPart.replace(/,/g, '') + decPart);
            return isFinite(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : match;
        });
        content = content.replace(/^\|(.+)\|[ \t]*$/gm, (line, cells) => {
            const parts = cells.split('|').map(c => c.trim());
            const formatted = parts.map(cell => {
                if (/^[-: ]+$/.test(cell)) return cell;
                if (/[_]/.test(cell) || cell === cell.toUpperCase()) return formatColumnHeader(cell);
                return cell;
            });
            return '| ' + formatted.join(' | ') + ' |';
        });
        return content;
    }

    function renderMarkdown(content) {
        return marked.parse(preProcessMarkdown(content), { breaks: true, gfm: true });
    }

    // ── Messages ──────────────────────────────────────────────────────
    function hideEmpty() { if (emptyState) emptyState.style.display = 'none'; }

    function addMessage(type, initialText, options = {}) {
        hideEmpty();
        const row    = document.createElement('div');
        row.className = 'chat-msg-row ' + (type === 'user' ? 'user' : 'bot');

        const avatar = document.createElement('div');
        avatar.className = 'chat-avatar';
        avatar.textContent = type === 'user' ? 'U' : 'AI';

        const bubble  = document.createElement('div');
        bubble.className = 'chat-bubble';

        const content = document.createElement('div');
        content.className = 'md';

        if (options.typing) {
            content.innerHTML = '<div class="typing-dots"><i></i><i></i><i></i><span class="typing-timer">0.0s</span></div>';
        } else if (type === 'user') {
            content.textContent = initialText || '';
        } else {
            content.innerHTML = renderMarkdown(initialText || '');
        }

        const meta = document.createElement('div');
        meta.className = 'msg-meta';

        bubble.appendChild(content);
        bubble.appendChild(meta);

        if (type === 'user') { row.appendChild(bubble); row.appendChild(avatar); }
        else                 { row.appendChild(avatar); row.appendChild(bubble); }

        chatWindow.appendChild(row);
        chatWindow.scrollTop = chatWindow.scrollHeight;
        return { row, bubble, content, meta };
    }

    // ── Chart helpers ─────────────────────────────────────────────────
    function isNumericValue(v) {
        if (v === null || v === undefined || v === '') return false;
        return Number.isFinite(Number.parseFloat(String(v)));
    }

    function getLatestRowsFromTrace(trace) {
        if (!Array.isArray(trace)) return [];
        for (let i = trace.length - 1; i >= 0; i--) {
            const r = trace[i] && trace[i].result;
            if (r && Array.isArray(r.rows) && r.rows.length > 0) return r.rows;
        }
        return [];
    }

    function buildChartPayload(trace, columnLabels) {
        let rows = null;
        for (let i = (trace || []).length - 1; i >= 0; i--) {
            const c = trace[i];
            if (c && c.result && Array.isArray(c.result.rows) && c.result.rows.length > 0) { rows = c.result.rows; break; }
        }
        if (!rows || rows.length === 0) return null;
        const keys = Object.keys(rows[0]);
        if (!keys.length) return null;
        let labelKey = keys.find(k => rows.some(r => !isNumericValue(r[k]))) || keys[0];
        let valueKeys = keys.filter(k => k !== labelKey && rows.some(r => isNumericValue(r[k])));
        if (!valueKeys.length && keys.length > 1 && rows.some(r => isNumericValue(r[keys[1]]))) valueKeys = [keys[1]];
        if (!valueKeys.length) return null;

        // Filter out ID / sequence / timestamp columns
        valueKeys = valueKeys.filter(k => !looksLikeIdColumn(k));
        if (!valueKeys.length) return null;

        // Aggregate rows that share the same label to prevent duplicate categories and huge un-grouped charts
        const aggregated = aggregateByLabel(rows, labelKey, valueKeys);

        // Hard cap at 15 unique labels for smooth chart rendering
        const MAX_CHART_ROWS = 15;
        const wasCapped = aggregated.length > MAX_CHART_ROWS;
        const capped = aggregated.slice(0, MAX_CHART_ROWS);

        return {
            labels: capped.map((r, i) => { const v = r.label !== undefined ? r.label : r[labelKey]; return (v === null || v === undefined || v === '') ? 'Item ' + (i + 1) : String(v); }),
            datasets: valueKeys.slice(0, 4).map(k => ({ label: (columnLabels && columnLabels[k]) || k.replace(/_/g, ' '), values: capped.map(r => { const p = Number.parseFloat(String(r[k])); return isFinite(p) ? p : 0; }) })),
            totalGroups: aggregated.length, wasCapped, maxRows: MAX_CHART_ROWS
        };
    }

    function buildProfessionalChart(canvas, chartType, labels, datasets, isPie) {
        // Destroy existing Chart instance on canvas to avoid memory leaks & resize loop
        if (typeof Chart !== 'undefined') {
            const existingChart = Chart.getChart(canvas);
            if (existingChart) existingChart.destroy();
        }

        const fmt = v => {
            if (typeof v !== 'number') return v;
            if (Math.abs(v) >= 1e6) return (v/1e6).toFixed(1)+'M';
            if (Math.abs(v) >= 1e3) return (v/1e3).toFixed(1)+'K';
            return Number.isInteger(v) ? v : parseFloat(v.toFixed(2));
        };
        const isHBar = chartType === '_hbar' || chartType === 'hbar' || chartType === 'horizontalBar', finalType = isHBar ? 'bar' : chartType;
        const n = labels.length;

        // Dynamically size the wrap container's height; the canvas itself stays 100% of it.
        const calcHeight = isPie ? 260 : isHBar ? Math.min(Math.max(n*28+60,180),480) : n<=6?240:n<=12?280:320;
        const wrap = canvas.parentNode;
        if (wrap) wrap.style.height = calcHeight + 'px';
        canvas.style.height = '100%';
        canvas.style.width = '100%';

        const palette = ['#0f766e','#0284c7','#7c3aed','#e11d48','#f59e0b','#16a34a','#db2777','#9333ea'];
        const styledDs = datasets.map((ds, i) => {
            const col = palette[i % palette.length];
            const dsValues = ds.values || ds.data || [];
            if (isPie) return { ...ds, data:dsValues, backgroundColor: labels.map((_,j) => palette[j%palette.length]+'e0'), borderColor:'#fff', borderWidth:2, hoverOffset:8 };
            if (finalType==='line') return { ...ds, data:dsValues, borderColor:col, backgroundColor:col+'20', borderWidth:2.5, pointBackgroundColor:col, pointBorderColor:'#fff', pointBorderWidth:2, pointRadius:n>20?2:4, pointHoverRadius:5, fill:true, tension:0.4 };
            return { ...ds, data:dsValues, backgroundColor:col+'cc', borderColor:col, borderWidth:1.5, borderRadius:4, borderSkipped:false, hoverBackgroundColor:col };
        });
        const yTicks = { font:{family:"'Segoe UI',sans-serif",size:11}, color:'#64748b', callback:v=>fmt(v) };
        return new Chart(canvas, {
            type: finalType,
            data: { labels, datasets: isPie ? [styledDs[0]] : styledDs },
            options: {
                indexAxis: isHBar ? 'y' : 'x', responsive:true, maintainAspectRatio:false,
                animation:{ duration:300, easing:'easeOutQuart' },
                layout:{ padding:{ top:4, bottom:4, left:4, right:12 } },
                plugins:{
                    legend:{
                        display:isPie||datasets.length>1, position:isPie?'right':'bottom',
                        labels:{
                            font:{family:"'Segoe UI',sans-serif",size:11}, color:'#334155', padding:12,
                            // usePointStyle:true + pointStyle:'circle' with no pointStyleWidth is deliberate:
                            // Chart.js's legend renderer draws a true circle only when pointStyleWidth is left
                            // undefined (radius-only ctx.arc() path); setting it to any fixed value distorts
                            // the marker into an oval.
                            usePointStyle:true, pointStyle:'circle', boxHeight:8,
                            // Resolve the chart-TYPE-specific generateLabels (pie/doughnut emit one legend
                            // item per slice) rather than the global default (one item per dataset, which
                            // collapses a pie's legend down to a single entry).
                            generateLabels: (chart) => {
                                const typeGenerator = Chart.overrides[chart.config.type] && Chart.overrides[chart.config.type].plugins && Chart.overrides[chart.config.type].plugins.legend && Chart.overrides[chart.config.type].plugins.legend.labels && Chart.overrides[chart.config.type].plugins.legend.labels.generateLabels;
                                const items = (typeGenerator || Chart.defaults.plugins.legend.labels.generateLabels)(chart);
                                items.forEach(item => { item.pointStyle = 'circle'; });
                                return items;
                            },
                        },
                    },
                    tooltip:{ backgroundColor:'#0f172a', titleColor:'#94a3b8', bodyColor:'#f8fafc', borderColor:'#1e293b', borderWidth:1, padding:9, cornerRadius:8,
                        callbacks:{ label:ctx => '  '+ctx.dataset.label+': '+fmt(isHBar ? ctx.parsed.x : (ctx.parsed.y != null ? ctx.parsed.y : ctx.parsed)) } }
                },
                scales: isPie ? {} : isHBar ? {
                    x:{ beginAtZero:true, grid:{color:'#f1f5f9'}, border:{color:'transparent'}, ticks:yTicks },
                    y:{ grid:{display:false}, border:{color:'#e2e8f0'}, ticks:{font:{family:"'Segoe UI',sans-serif",size:11},color:'#64748b'} }
                } : {
                    x:{ grid:{display:false}, border:{color:'#e2e8f0'}, ticks:{font:{family:"'Segoe UI',sans-serif",size:11},color:'#64748b',maxRotation:n>8?45:0,autoSkip:true,maxTicksLimit:Math.min(n,15)} },
                    y:{ beginAtZero:true, grid:{color:'#f1f5f9',lineWidth:1}, border:{dash:[4,4],color:'transparent'}, ticks:yTicks }
                }
            }
        });
    }

    // Returns true if a column looks like an ID / sequence number (not a plottable amount), judged
    // by column name alone. A "small, all-positive integer values" range check used to also
    // disqualify a column, but that's exactly the shape of any COUNT()/GROUP BY aggregate — the most
    // common chart-worthy query — so it was silently killing the chart for almost every "X by Y" count.
    function looksLikeIdColumn(col) {
        return /^(id|_id|number|no|num|customer|supplier|company|contact|assigned|created_by|modified_by|visibility|alternative_id)$/i.test(col.trim());
    }

    // Slice and sum pie/doughnut data down to top 7 + "Others" once there are more than 8 slices.
    function preparePieData(labels, datasets) {
        if (!labels || labels.length <= 8) return { labels, datasets };
        const topLabels = labels.slice(0, 7);
        const remainingCount = labels.length - 7;
        topLabels.push('Others (' + remainingCount + ' items)');
        const newDatasets = datasets.map(ds => {
            const rawArray = ds.values || ds.data || [];
            const topValues = rawArray.slice(0, 7);
            const restSum = rawArray.slice(7).reduce((acc, cur) => acc + (Number.isFinite(cur) ? cur : 0), 0);
            topValues.push(Math.round(restSum * 100) / 100);
            return { ...ds, values: topValues, data: topValues };
        });
        return { labels: topLabels, datasets: newDatasets };
    }

    function aggregateByLabel(rows, labelCol, valueCols) {
        const map = new Map();
        for (const row of rows) {
            const lbl = String(row[labelCol] != null ? row[labelCol] : '');
            if (!map.has(lbl)) { const e={label:lbl}; for(const c of valueCols) e[c]=0; map.set(lbl,e); }
            const e = map.get(lbl);
            for (const c of valueCols) { const v=parseFloat(String(row[c] != null ? row[c] : 0)); e[c]+=isFinite(v)?v:0; }
        }
        return Array.from(map.values());
    }

    function renderAgentChart(msg, chartConfig, trace) {
        if (!chartConfig || typeof Chart === 'undefined') return;
        const allRows = getLatestRowsFromTrace(trace);
        if (!allRows.length) return;
        const { type:rawType, title, labelColumn, valueColumns:rawValueCols } = chartConfig;
        const valueCols = (rawValueCols||[]).filter(c => !looksLikeIdColumn(c));
        if (!valueCols.length) return;
        const aggregated = aggregateByLabel(allRows, labelColumn, valueCols);
        const MAX = 15, wasCapped = aggregated.length > MAX, rows = aggregated.slice(0, MAX);
        const n = rows.length;
        let type = rawType;
        if (type==='bar'&&n>6) type='_hbar';
        const isPie = type==='pie'||type==='doughnut';
        let labels = rows.map(r=>String(r[labelColumn] != null ? r[labelColumn] : ''));
        let datasets = valueCols.slice(0,4).map(c=>{
            const vals = rows.map(r=>{const v=parseFloat(String(r[c] != null ? r[c] : 0));return isFinite(v)?v:0;});
            return { label:c.replace(/_/g,' '), values:vals, data:vals };
        });
        if (isPie) { const sliced = preparePieData(labels, datasets); labels = sliced.labels; datasets = sliced.datasets; }
        if (msg.chartInstance) { msg.chartInstance.destroy(); msg.chartInstance = null; }
        const existing = msg.bubble.querySelector('.chart-card');
        if (existing) existing.remove();
        const card = document.createElement('div'); card.className='chart-card';
        if (title) { const h=document.createElement('div'); h.className='chart-title'; h.textContent=title; card.appendChild(h); }
        const canvasWrap = document.createElement('div'); canvasWrap.className = isPie ? 'chart-canvas-wrap pie-wrap' : 'chart-canvas-wrap';
        const canvas = document.createElement('canvas'); canvas.className='chart-canvas'; canvasWrap.appendChild(canvas); card.appendChild(canvasWrap);
        if (wasCapped) { const note=document.createElement('div'); note.className='chart-note'; note.textContent='Showing top '+MAX+' of '+aggregated.length+' groups.'; card.appendChild(note); }
        msg.bubble.appendChild(card);
        msg.chartInstance = buildProfessionalChart(canvas, type, labels, datasets, isPie);
    }

    function renderChartFromTrace(msg, trace, graphType) {
        const existingChart = msg.bubble.querySelector('.chart-card');

        if (!graphType || graphType === 'none' || typeof Chart === 'undefined') {
            if (existingChart) existingChart.remove();
            if (msg.chartInstance) { msg.chartInstance.destroy(); msg.chartInstance = null; }
            return;
        }

        const payload = buildChartPayload(trace, msg.columnLabels);
        if (!payload) {
            if (existingChart) existingChart.remove();
            if (msg.chartInstance) { msg.chartInstance.destroy(); msg.chartInstance = null; }
            return;
        }

        if (msg.chartInstance) { msg.chartInstance.destroy(); msg.chartInstance = null; }
        if (existingChart) existingChart.remove();

        let activeType = graphType;
        const isPie = activeType === 'pie' || activeType === 'doughnut';

        let renderLabels = payload.labels;
        let renderDatasets = payload.datasets.map(ds => ({ label: ds.label, values: ds.values, data: ds.values }));

        if (isPie) {
            const sliced = preparePieData(renderLabels, renderDatasets);
            renderLabels = sliced.labels; renderDatasets = sliced.datasets;
        } else if (activeType === 'bar' && renderLabels.length > 8) {
            activeType = '_hbar';
        }

        const chartCard = document.createElement('div'); chartCard.className = 'chart-card';

        // Header with title + interactive chart-type switcher
        const headerDiv = document.createElement('div'); headerDiv.className = 'chart-toolbar-header';
        const titleEl = document.createElement('div'); titleEl.className = 'chart-title-text';
        titleEl.textContent = isPie
            ? (activeType === 'doughnut' ? '🍩 Doughnut Distribution' : '🥧 Pie Distribution')
            : (activeType === 'line' ? '📈 Trend Analysis' : '📊 Data Overview');
        headerDiv.appendChild(titleEl);

        const typeSelector = document.createElement('div'); typeSelector.className = 'chart-type-selector';
        const typeOptions = [
            { type: 'bar', label: '📊 Bar' },
            { type: 'line', label: '📈 Line' },
            { type: 'pie', label: '🥧 Pie' },
            { type: 'doughnut', label: '🍩 Doughnut' },
            { type: 'hbar', label: '📑 H-Bar' },
        ];
        typeOptions.forEach(opt => {
            const btn = document.createElement('button'); btn.type = 'button';
            const isCurrentActive = (activeType === opt.type) || (activeType === '_hbar' && opt.type === 'hbar') || (activeType === '_hbar' && opt.type === 'bar' && renderLabels.length > 8);
            btn.className = 'chart-type-btn' + (isCurrentActive ? ' active' : '');
            btn.textContent = opt.label;
            btn.addEventListener('click', () => renderChartFromTrace(msg, trace, opt.type));
            typeSelector.appendChild(btn);
        });
        headerDiv.appendChild(typeSelector);
        chartCard.appendChild(headerDiv);

        const canvasWrap = document.createElement('div'); canvasWrap.className = isPie ? 'chart-canvas-wrap pie-wrap' : 'chart-canvas-wrap';
        const canvas = document.createElement('canvas'); canvas.className = 'chart-canvas';
        canvasWrap.appendChild(canvas); chartCard.appendChild(canvasWrap);

        if (payload.wasCapped) {
            const note = document.createElement('div'); note.className = 'chart-note';
            note.textContent = 'Showing top ' + payload.maxRows + ' of ' + payload.totalGroups + ' groups.';
            chartCard.appendChild(note);
        }

        msg.bubble.appendChild(chartCard);
        msg.chartInstance = buildProfessionalChart(canvas, activeType, renderLabels, renderDatasets, isPie);
    }

    // ── Export ────────────────────────────────────────────────────────
    function sanitizeFileStamp(v) { return String(v||'').replace(/[^a-z0-9_-]+/gi,'-').replace(/^-+|-+$/g,'').slice(0,48)||'answer'; }

    function triggerDownload(fileName, content, mimeType) {
        const blob=new Blob([content],{type:mimeType}), url=URL.createObjectURL(blob), a=document.createElement('a');
        a.href=url; a.download=fileName; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    }

    function toCsvValue(v) {
        if(v===null||v===undefined) return '';
        const s=String(v); return /[",\n]/.test(s) ? '"'+s.replace(/"/g,'""')+'"' : s;
    }

    function buildCsvFromRows(rows) {
        if (!Array.isArray(rows)||!rows.length) return 'section,value\nanswer_rows,0\n';
        const h=Object.keys(rows[0]||{});
        return [h.join(','), ...rows.map(r=>h.map(k=>toCsvValue(r[k])).join(','))].join('\n');
    }

    function buildHtmlReport(payload, nowIso) {
        const rows=getLatestRowsFromTrace(payload.trace), hdr=rows.length?Object.keys(rows[0]):[], bodyR=rows.slice(0,100);
        const esc=v=>String(v===null||v===undefined?'':v).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
        const tbl=!hdr.length?'<p>No SQL rows in trace.</p>':'<table><thead><tr>'+hdr.map(h=>'<th>'+esc(h)+'</th>').join('')+'</tr></thead><tbody>'+bodyR.map(r=>'<tr>'+hdr.map(h=>'<td>'+esc(r[h])+'</td>').join('')+'</tr>').join('')+'</tbody></table>';
        return '<!doctype html><html><head><meta charset="utf-8"/><title>FreeLife AI Report</title><style>body{font-family:Segoe UI,Arial,sans-serif;margin:24px;color:#0f172a}h1{margin:0 0 8px}h2{margin:22px 0 10px}.meta{color:#475569;margin-bottom:14px}.box{border:1px solid #e2e8f0;border-radius:8px;padding:12px;background:#f8fafc}table{border-collapse:collapse;width:100%;font-size:13px}th,td{border:1px solid #e2e8f0;padding:8px;text-align:left}th{background:#f1f5f9}</style></head><body><h1>FreeLife AI Report</h1><div class="meta">Exported at: '+esc(nowIso)+'</div><h2>Question</h2><div class="box">'+esc(payload.question||'')+'</div><h2>Answer</h2><div class="box">'+esc(payload.answer||'')+'</div><h2>Data Preview</h2>'+tbl+'</body></html>';
    }

    function exportLatestAnswer() {
        if (!latestExportPayload || !latestExportPayload.answer) { alert('No answer to export yet.'); return; }
        const format=exportFormatSelect.value, now=new Date(), stamp=now.toISOString().replace(/[:.]/g,'-'), base=sanitizeFileStamp(latestExportPayload.question||'answer');
        if (format==='json') { triggerDownload(base+'-'+stamp+'.json', JSON.stringify({question:latestExportPayload.question||'',answer:latestExportPayload.answer||'',trace:latestExportPayload.trace||[],exportedAt:now.toISOString()},null,2),'application/json;charset=utf-8'); return; }
        if (format==='md')   { triggerDownload(base+'-'+stamp+'.md', ['# FreeLife AI Export','','## Question',latestExportPayload.question||'','','## Answer',latestExportPayload.answer||'','','Exported at: '+now.toISOString()].join('\n'),'text/markdown;charset=utf-8'); return; }
        if (format==='csv')  { triggerDownload(base+'-'+stamp+'.csv', buildCsvFromRows(getLatestRowsFromTrace(latestExportPayload.trace)),'text/csv;charset=utf-8'); return; }
        if (format==='pdf')  {
            if (!window.jspdf || !window.jspdf.jsPDF) { alert('PDF library not loaded.'); return; }
            const {jsPDF}=window.jspdf, doc=new jsPDF({unit:'pt',format:'a4'}), pageW=doc.internal.pageSize.getWidth(), m=40, maxW=pageW-m*2; let y=48;
            const wrap=(lbl,txt)=>{ doc.setFont('helvetica','bold'); doc.setFontSize(12); doc.text(lbl,m,y); y+=18; doc.setFont('helvetica','normal'); doc.setFontSize(10); const lines=doc.splitTextToSize(String(txt||''),maxW); doc.text(lines,m,y); y+=lines.length*14+14; if(y>760){doc.addPage();y=48;} };
            doc.setFont('helvetica','bold'); doc.setFontSize(16); doc.text('FreeLife AI Export',m,y); y+=24;
            wrap('Question',latestExportPayload.question||''); wrap('Answer',latestExportPayload.answer||''); wrap('Exported at',now.toISOString());
            doc.save(base+'-'+stamp+'.pdf'); return;
        }
        if (format==='report') { triggerDownload(base+'-'+stamp+'.html', buildHtmlReport(latestExportPayload,now.toISOString()),'text/html;charset=utf-8'); return; }
        triggerDownload(base+'-'+stamp+'.txt', ['Question:',latestExportPayload.question||'','','Answer:',latestExportPayload.answer||'','','Exported at: '+now.toISOString()].join('\n'),'text/plain;charset=utf-8');
    }

    // ── Bubble actions ────────────────────────────────────────────────
    function setBotContent(msg, text, isProgress) {
        msg.content[isProgress ? 'textContent' : 'innerHTML'] = isProgress ? text : renderMarkdown(text);
        chatWindow.scrollTop = chatWindow.scrollHeight;
    }

    function attachTraceButton(msg, steps) {
        if (!steps || !steps.length || msg.meta.querySelector('.msg-trace-btn')) return;
        const btn=document.createElement('button'); btn.type='button'; btn.className='msg-trace-btn'; btn.textContent='View reasoning';
        btn.addEventListener('click', ()=>openTrace(steps)); msg.meta.appendChild(btn);
    }

    function attachExportButton(msg, answerText, questionText) {
        if (!answerText||msg.meta.querySelector('.inline-export-btn')) return;
        const btn=document.createElement('button'); btn.type='button'; btn.className='msg-trace-btn inline-export-btn'; btn.textContent='Export';
        btn.addEventListener('click', ()=>{ const now=new Date(),stamp=now.toISOString().replace(/[:.]/g,'-'),base=sanitizeFileStamp(questionText||'answer'); triggerDownload(base+'-'+stamp+'.txt',answerText,'text/plain;charset=utf-8'); });
        msg.meta.appendChild(btn);
    }

    function attachCsvButton(msg, csvUrl) {
        if (!csvUrl||msg.meta.querySelector('.msg-csv-btn')) return;
        const a=document.createElement('a'); a.className='msg-csv-btn'; a.href=csvUrl; a.target='_blank'; a.rel='noopener noreferrer'; a.textContent='Download CSV';
        msg.meta.appendChild(a);
    }

    function attachFeedbackButtons(msg, analyticsTimestamp) {
        if (!analyticsTimestamp || msg.meta.querySelector('.feedback-group')) return;
        const group = document.createElement('span');
        group.className = 'feedback-group';
        group.style.display = 'inline-flex'; group.style.gap = '4px'; group.style.marginLeft = '4px';

        const submitFeedback = async (feedback, upBtn, downBtn) => {
            upBtn.disabled = true; downBtn.disabled = true;
            try {
                await fetch(API_BASE + '/api/analytics/feedback', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': CSRF_TOKEN },
                    body: JSON.stringify({ timestamp: analyticsTimestamp, feedback, tenantDb: getStoredTenantDb() || undefined, userId: getCurrentUserId() || undefined })
                });
                (feedback === 'correct' ? upBtn : downBtn).style.opacity = '1';
                (feedback === 'correct' ? downBtn : upBtn).style.opacity = '0.35';
            } catch (err) {
                upBtn.disabled = false; downBtn.disabled = false;
            }
        };

        const upBtn = document.createElement('button'); upBtn.type = 'button'; upBtn.className = 'msg-trace-btn feedback-btn'; upBtn.title = 'Mark this answer correct'; upBtn.innerHTML = '👍';
        const downBtn = document.createElement('button'); downBtn.type = 'button'; downBtn.className = 'msg-trace-btn feedback-btn'; downBtn.title = 'Mark this answer wrong'; downBtn.innerHTML = '👎';
        upBtn.addEventListener('click', () => submitFeedback('correct', upBtn, downBtn));
        downBtn.addEventListener('click', () => submitFeedback('wrong', upBtn, downBtn));
        group.appendChild(upBtn); group.appendChild(downBtn);
        msg.meta.appendChild(group);
    }

    function renderDataTableComponent(msg, data) {
        if (!data) return;
        let columns = [];
        let rows = [];

        if (data.table && Array.isArray(data.table.columns) && Array.isArray(data.table.rows)) {
            columns = data.table.columns;
            rows = data.table.rows.map(rArray => {
                const obj = {};
                columns.forEach((col, idx) => { obj[col] = rArray[idx]; });
                return obj;
            });
        } else if (Array.isArray(data.result) && data.result.length > 0) {
            rows = data.result;
            columns = Object.keys(rows[0]);
        } else {
            return;
        }

        if (rows.length === 0 || columns.length === 0) return;

        function formatCell(val, colName) {
            if (val === null || val === undefined || val === '') return '-';
            const strVal = String(val);
            const colStr = String(colName || '');
            // Normalize raw ISO 8601 timestamps to a plain "YYYY-MM-DD HH:MM:SS" reading. Date-only
            // values (no time component) are left as-is since they're already in the desired shape.
            const isoTimestampMatch = strVal.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/);
            if (isoTimestampMatch) return isoTimestampMatch[1] + ' ' + isoTimestampMatch[2];
            const idLike = /^(id|year|phone|mobile|zip|postcode|tax_number|vat|number|_id)$/i;
            if (typeof val === 'number') {
                if (idLike.test(colStr)) return strVal;
                return val.toLocaleString('en-US', { maximumFractionDigits: 2 });
            }
            const num = Number.parseFloat(strVal);
            if (Number.isFinite(num) && !/^\d{4}-\d{2}-\d{2}/.test(strVal)) {
                if (idLike.test(colStr)) return strVal;
                return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
            }
            return strVal;
        }

        const existing = msg.bubble.querySelector('.sheet-wrap');
        if (existing) existing.remove();

        const wrap = document.createElement('div');
        wrap.className = 'sheet-wrap';
        wrap.style.marginTop = '12px';
        wrap.style.border = '1px solid #cbd5e1';
        wrap.style.borderRadius = '10px';
        wrap.style.overflow = 'hidden';
        wrap.style.background = '#ffffff';

        // Still needed by the Export CSV button below, which re-runs this SQL server-side.
        const executedSql = data.sql || (data.trace && (data.trace.find(t => t.sql) || {}).sql) || '';

        // SQL query badge — disabled per request, kept for re-enabling later.
        // if (executedSql) {
        //     const sqlBadge = document.createElement('div');
        //     sqlBadge.style.padding = '8px 12px';
        //     sqlBadge.style.background = '#f0fdf4';
        //     sqlBadge.style.borderBottom = '1px solid #bbf7d0';
        //     sqlBadge.style.color = '#16a34a';
        //     sqlBadge.style.fontSize = '12px';
        //     sqlBadge.style.fontWeight = '600';
        //     sqlBadge.title = executedSql;
        //     sqlBadge.innerHTML = '✓ SQL Query Executed <code style="font-size:11px;color:#334155;margin-left:8px;font-weight:normal;background:#e2e8f0;padding:2px 6px;border-radius:4px;">' + executedSql.slice(0, 75) + '...</code>';
        //     wrap.appendChild(sqlBadge);
        // }

        // Collapsible SQL reasoning panel — disabled per request, kept for re-enabling later.
        const sqlReasoning = '';
        if (sqlReasoning) {
            const reasoningPanel = document.createElement('div'); reasoningPanel.className = 'sql-reasoning-panel';
            const header = document.createElement('div'); header.className = 'sql-reasoning-header';
            const chevron = document.createElement('span'); chevron.className = 'sql-reasoning-chevron'; chevron.textContent = '▼';
            header.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0369a1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>SQL Reasoning — why the AI built this query';
            header.appendChild(chevron);

            const body = document.createElement('div'); body.className = 'sql-reasoning-body';
            const lines = sqlReasoning.split('\n').map(l => l.trim()).filter(Boolean);
            const hasBullets = lines.some(l => /^[•\-\*]/.test(l));
            if (hasBullets) {
                const ul = document.createElement('ul');
                lines.forEach(line => {
                    const li = document.createElement('li');
                    li.textContent = line.replace(/^[•\-\*]\s*/, '');
                    ul.appendChild(li);
                });
                body.appendChild(ul);
            } else {
                const pre = document.createElement('div'); pre.className = 'reasoning-raw'; pre.textContent = sqlReasoning;
                body.appendChild(pre);
            }

            let isOpen = false;
            header.addEventListener('click', () => {
                isOpen = !isOpen;
                body.classList.toggle('open', isOpen);
                chevron.style.transform = isOpen ? 'rotate(180deg)' : '';
            });

            reasoningPanel.appendChild(header);
            reasoningPanel.appendChild(body);
            wrap.appendChild(reasoningPanel);
        }

        // Toolbar (stats + export CSV)
        const toolbar = document.createElement('div');
        toolbar.style.display = 'flex'; toolbar.style.justifyContent = 'space-between'; toolbar.style.alignItems = 'center';
        toolbar.style.padding = '8px 12px'; toolbar.style.background = '#f8fafc'; toolbar.style.borderBottom = '1px solid #e2e8f0';

        const statsEl = document.createElement('span');
        statsEl.style.fontWeight = '600'; statsEl.style.fontSize = '13px'; statsEl.style.color = '#475569';
        statsEl.innerHTML = '📊 Stats: <b>' + rows.length + '</b> rows × <b>' + columns.length + '</b> columns';

        const csvBtn = document.createElement('button');
        csvBtn.type = 'button';
        csvBtn.style.padding = '5px 12px'; csvBtn.style.fontSize = '12px'; csvBtn.style.fontWeight = '600';
        csvBtn.style.background = '#0f766e'; csvBtn.style.color = '#ffffff'; csvBtn.style.border = 'none';
        csvBtn.style.borderRadius = '6px'; csvBtn.style.cursor = 'pointer';
        csvBtn.innerHTML = '📥 Export CSV';

        csvBtn.addEventListener('click', async () => {
            const fallbackDownload = (fileName) => {
                const csvContent = [columns.join(',')].concat(rows.map(r => columns.map(c => '"' + String(r[c] != null ? r[c] : '').replace(/"/g, '""') + '"').join(','))).join('\n');
                triggerDownload(fileName, csvContent, 'text/csv;charset=utf-8');
            };
            if (!executedSql) { fallbackDownload('query_results.csv'); return; }

            const originalText = csvBtn.innerHTML;
            csvBtn.innerHTML = '⏳ Exporting...'; csvBtn.disabled = true;
            try {
                const tenantDb = getStoredTenantDb();
                const res = await fetch(API_BASE + '/api/export-csv', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': CSRF_TOKEN },
                    body: JSON.stringify({ tenantDb, sql: executedSql, userId: getCurrentUserId() || undefined })
                });
                if (!res.ok) throw new Error(await res.text());
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'query_results.csv';
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                console.error('Export failed:', e);
                fallbackDownload('query_results_partial.csv');
            } finally {
                csvBtn.innerHTML = originalText; csvBtn.disabled = false;
            }
        });

        toolbar.appendChild(statsEl);
        toolbar.appendChild(csvBtn);
        wrap.appendChild(toolbar);

        // Table
        const scroller = document.createElement('div');
        scroller.style.overflowX = 'auto'; scroller.style.maxHeight = '350px';

        const table = document.createElement('table');
        table.className = 'sheet-table';
        table.style.width = '100%'; table.style.borderCollapse = 'collapse'; table.style.fontSize = '13px';

        const idLikeCol = /^(id|year|phone|mobile|zip|postcode|tax_number|vat|number|_id|.*_id)$/i;
        function isNumericColumn(col) {
            if (idLikeCol.test(String(col))) return false;
            const sample = rows.slice(0, 100).map(r => r[col]).filter(v => v !== null && v !== undefined && v !== '');
            if (sample.length === 0) return false;
            const numericCount = sample.filter(v => typeof v === 'number' || (typeof v === 'string' && Number.isFinite(Number.parseFloat(v)) && /^-?[\d,]*\.?\d+$/.test(v.trim()))).length;
            return numericCount / sample.length > 0.8;
        }
        const numericCols = new Set(columns.filter(isNumericColumn));

        // Prefer the tenant's own field Label (server-computed) over generic snake_case formatting.
        function titleCase(col) {
            if (data.columnLabels && data.columnLabels[col]) return data.columnLabels[col];
            return String(col).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        }

        const thead = document.createElement('thead');
        const headerTr = document.createElement('tr');
        columns.forEach(col => {
            const th = document.createElement('th');
            th.textContent = titleCase(col);
            if (numericCols.has(col)) th.classList.add('col-numeric');
            th.style.padding = '8px 12px'; th.style.background = '#f1f5f9'; th.style.color = '#0f172a';
            th.style.fontWeight = '600'; th.style.borderBottom = '2px solid #cbd5e1';
            th.style.textAlign = numericCols.has(col) ? 'right' : 'left';
            headerTr.appendChild(th);
        });
        thead.appendChild(headerTr);
        table.appendChild(thead);

        const tbody = document.createElement('tbody');
        rows.slice(0, 100).forEach(r => {
            const tr = document.createElement('tr');
            columns.forEach(col => {
                const td = document.createElement('td');
                td.textContent = formatCell(r[col], col);
                if (numericCols.has(col)) td.classList.add('cell-numeric');
                td.style.padding = '7px 12px'; td.style.borderBottom = '1px solid #e2e8f0';
                tr.appendChild(td);
            });
            tbody.appendChild(tr);
        });
        table.appendChild(tbody);
        scroller.appendChild(table);

        wrap.appendChild(scroller);
        msg.bubble.appendChild(wrap);
    }

    // ── Trace modal ───────────────────────────────────────────────────
    function openTrace(steps) {
        traceList.innerHTML='';
        steps.forEach((step, idx) => {
            const item=document.createElement('div'); item.className='fl-trace-item';
            const title=document.createElement('div'); title.className='fl-trace-title'; title.textContent='Step '+(step.step||idx+1)+' - '+(step.action||step.toolAction||'action'); item.appendChild(title);
            if (step.thought) { const t=document.createElement('div'); t.className='fl-trace-thought'; t.textContent=step.thought; item.appendChild(t); }
            if (step.query)   { const q=document.createElement('pre'); q.className='fl-trace-query'; q.textContent=step.query; item.appendChild(q); }
            if (step.error)   { const e=document.createElement('div'); e.className='fl-trace-error'; e.textContent='Error: '+step.error; item.appendChild(e); }
            if (step.llmMs!=null||step.toolMs!=null) {
                const timing=document.createElement('div'); timing.className='fl-trace-timing';
                if (step.llmMs!=null) { const c=document.createElement('span'); c.className='fl-trace-timing-chip llm'; c.innerHTML='🤖 LLM&nbsp;<strong>'+(step.llmMs/1000).toFixed(2)+'s</strong>'; timing.appendChild(c); }
                if (step.toolMs!=null) { const c=document.createElement('span'); c.className='fl-trace-timing-chip sql'; c.innerHTML=(step.action==='sql'?'🗄️ SQL':'🔍 Analyst')+'&nbsp;<strong>'+(step.toolMs/1000).toFixed(2)+'s</strong>'; timing.appendChild(c); }
                item.appendChild(timing);
            }
            traceList.appendChild(item);
        });
        traceModal.style.display='flex'; traceModal.setAttribute('aria-hidden','false');
    }

    function closeTrace() { traceModal.style.display='none'; traceModal.setAttribute('aria-hidden','true'); }

    // ── Streaming chat ───────────────────────────────────────────────
    // Consumes the streaming /api/chat SSE endpoint and reconstructs the same response shape the
    // old /api/chat/sync call returned in one shot (trace, table, tokens, analyticsTimestamp, etc.)
    // so every post-answer feature (step breakdown, CSV export, feedback, charts, table grid) keeps
    // working unchanged. The only new behavior is live status text as each event arrives.
    async function streamChatAndBuildData(payload, setLiveStatus) {
        const response = await fetch(API_BASE + '/api/chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': CSRF_TOKEN },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            throw new Error(errBody.error || 'Unable to reach chat endpoint');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const trace = [];
        let stepCount = 1;
        let tableDetails, rewrittenQuery, finalSqlReasoning, finalResult, columnLabels;
        let finalAnswer = '', finalSql = '', finalLatency = {};
        let call1Analytics, call2Analytics, analyticsTimestamp;
        let sawFinal = false, chunkAnswer = '', chunkSql, chunkTokens, errorMsg;

        const handleEvent = (event) => {
            switch (event.type) {
                case 'rewritten_query':
                    rewrittenQuery = event.query;
                    trace.push({ step: stepCount++, action: 'Query Optimization', query: event.query });
                    setLiveStatus('Understanding your question…');
                    break;
                case 'table_retrieved': {
                    tableDetails = event.tableDetails;
                    const tables = event.tables || [];
                    trace.push({ step: stepCount++, action: 'Table Retrieval', thought: 'Identified relevant tables: ' + tables.join(', ') });
                    setLiveStatus(tables.length ? 'Looking at ' + tables.join(', ') + '…' : 'Looking at your data…');
                    break;
                }
                case 'sql_generated':
                    finalSql = event.sql;
                    if (event.sqlReasoning) finalSqlReasoning = event.sqlReasoning;
                    trace.push({ step: stepCount++, action: 'SQL Generation', query: event.sql, reasoning: event.sqlReasoning });
                    setLiveStatus('Writing the database query…');
                    break;
                case 'thought':
                    setLiveStatus(event.content || 'Working on it…');
                    break;
                case 'final':
                    sawFinal = true;
                    if (event.answer) finalAnswer = event.answer;
                    if (event.sql) finalSql = event.sql;
                    finalResult = event.result;
                    if (event.latencyMs) finalLatency = event.latencyMs;
                    call1Analytics = event.call1Analytics;
                    call2Analytics = event.call2Analytics;
                    analyticsTimestamp = event.analyticsTimestamp;
                    if (event.columnLabels) columnLabels = event.columnLabels;
                    trace.push({
                        step: stepCount++, action: 'Execution & Summary', thought: event.answer,
                        result: { rows: event.result },
                        llmMs: event.latencyMs && event.latencyMs.nlSummary,
                        toolMs: event.latencyMs && event.latencyMs.dbExecution
                    });
                    setLiveStatus('Summarizing the results…');
                    break;
                case 'final-chunk':
                    chunkAnswer += (event.chunk || '');
                    break;
                case 'sql':
                    chunkSql = event.content;
                    break;
                case 'result':
                    finalResult = event.content;
                    break;
                case 'tokens':
                    chunkTokens = event.content;
                    if (event.analyticsTimestamp) analyticsTimestamp = event.analyticsTimestamp;
                    break;
                case 'error':
                    errorMsg = event.message || 'Something went wrong.';
                    if (event.analyticsTimestamp) analyticsTimestamp = event.analyticsTimestamp;
                    break;
                default:
                    break;
            }
        };

        setLiveStatus('Thinking…');

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() || '';
            for (const part of parts) {
                const line = part.trim();
                if (!line.startsWith('data:')) continue;
                try { handleEvent(JSON.parse(line.slice(5).trim())); } catch (e) { /* ignore partial SSE line */ }
            }
        }

        if (errorMsg) return { error: errorMsg, analyticsTimestamp };

        // The standard SQL path emits a `final` event covering everything. Other intents (chat
        // summary, semantic search, theme clustering) stream final-chunk/sql/result/tokens instead.
        if (!sawFinal) {
            finalAnswer = chunkAnswer || finalAnswer;
            if (chunkSql) finalSql = chunkSql;
        }

        let tablePayload = null;
        if (Array.isArray(finalResult) && finalResult.length > 0) {
            const columns = Object.keys(finalResult[0]);
            const rows = finalResult.map(r => columns.map(col => (r[col] !== undefined ? r[col] : null)));
            tablePayload = { columns, rows };
        }

        const sqlIn = (call1Analytics && call1Analytics.input_tokens) || (chunkTokens && chunkTokens.sql_in) || 0;
        const sqlOut = (call1Analytics && call1Analytics.output_tokens) || (chunkTokens && chunkTokens.sql_out) || 0;
        const summaryIn = (call2Analytics && call2Analytics.input_tokens) || (chunkTokens && chunkTokens.summary_in) || 0;
        const summaryOut = (call2Analytics && call2Analytics.output_tokens) || (chunkTokens && chunkTokens.summary_out) || 0;
        const totalCostVal = ((call1Analytics && call1Analytics.cost) || 0) + ((call2Analytics && call2Analytics.cost) || 0);

        return {
            answer: finalAnswer,
            rewrittenQuery: rewrittenQuery || payload.message,
            table: tablePayload,
            sql: finalSql || '',
            tokens: { sql_in: sqlIn, sql_out: sqlOut, summary_in: summaryIn, summary_out: summaryOut, total_tokens: sqlIn+sqlOut+summaryIn+summaryOut, total_cost: '$' + totalCostVal.toFixed(6) },
            result: finalResult,
            tableDetails,
            latencyMs: finalLatency,
            trace,
            sqlReasoning: finalSqlReasoning,
            analyticsTimestamp,
            columnLabels
        };
    }

    // ── Send message ──────────────────────────────────────────────────
    async function sendMessage() {
        const question = userInput.value.trim();
        if (!question||isSending) return;

        const graphType = graphTypeSelect ? graphTypeSelect.value : 'none';
        const includeHint = graphInAnswerCheck ? graphInAnswerCheck.checked : false;
        const questionToSend = includeHint&&graphType!=='none' ? question+' Also include a short chart summary suitable for a '+graphType+' graph.' : question;

        isSending=true; userInput.value=''; sendBtn.disabled=true;
        addMessage('user', question);
        const botMsg = addMessage('bot', '', { typing:true });

        // ── Live status text (replaces the old ticking-seconds counter) ────
        const startTime=Date.now();
        const timerEl=botMsg.content.querySelector('.typing-timer');
        const updateLiveStatus = (text) => { if (timerEl) timerEl.textContent = text; };

        try {
            const selectedModel = (document.getElementById('model-select') || {}).value || undefined;

            const data = await streamChatAndBuildData({
                message: questionToSend,
                sessionId,
                tenantDb: getStoredTenantDb() || undefined,
                userId: getCurrentUserId() || undefined,
                model: selectedModel
            }, updateLiveStatus);

            const backendMs=Date.now()-startTime;

            if (data.error) {
                setBotContent(botMsg, 'Error: '+data.error, true);
                attachFeedbackButtons(botMsg, data.analyticsTimestamp);
            } else {
                const renderStart=Date.now();
                const answer=data.answer||'Done.';
                setBotContent(botMsg, answer, false);
                // Reasoning-trace button — disabled per request, kept for re-enabling later.
                // attachTraceButton(botMsg, data.trace||[]);
                attachExportButton(botMsg, answer, question);
                attachFeedbackButtons(botMsg, data.analyticsTimestamp);
                // Stashed on the message object (not just passed inline) so the chart-type-switcher
                // buttons — which re-call renderChartFromTrace later with only (msg, trace, newType) —
                // still have it available for the re-render.
                botMsg.columnLabels = data.columnLabels;
                renderDataTableComponent(botMsg, data);
                renderChartFromTrace(botMsg, data.trace||[], graphType);
                if (data.chartConfig) renderAgentChart(botMsg, data.chartConfig, data.trace||[]);
                latestExportPayload={question,answer,trace:data.trace||[]};
                if (exportBtn) exportBtn.disabled=false;
                if (data.csvUrl) attachCsvButton(botMsg, data.csvUrl);

                requestAnimationFrame(()=>{
                    const renderMs=Date.now()-renderStart, totalMs=Date.now()-startTime;
                    // Total-time badge and the Backend/Render/Step timing breakdown — disabled per
                    // request, kept commented out for re-enabling later.
                    // const timeBadge=document.createElement('span'); timeBadge.className='time-badge';
                    // timeBadge.title='Backend: '+(backendMs/1000).toFixed(2)+'s  |  Render: '+renderMs+'ms';
                    // timeBadge.innerHTML='&#9201; '+(totalMs/1000).toFixed(2)+'s'; botMsg.meta.prepend(timeBadge);

                    if (data.usage) {
                        const usageBadge=document.createElement('span'); usageBadge.className='time-badge';
                        usageBadge.style.backgroundColor='#10b981'; usageBadge.style.color='#fff';
                        usageBadge.title='Cached: '+data.usage.cached+' | Total: '+data.usage.total;
                        usageBadge.innerHTML='&#9889; Cached: '+data.usage.cached;
                        botMsg.meta.prepend(usageBadge);
                    }

                    // const trace=data.trace||[];
                    // const breakdown=document.createElement('div'); breakdown.className='time-breakdown';
                    // const beChip=document.createElement('span'); beChip.className='time-breakdown-chip'; beChip.innerHTML='<span class="chip-label">Backend</span>&nbsp;🖥️ '+(backendMs/1000).toFixed(2)+'s'; breakdown.appendChild(beChip);
                    // const feChip=document.createElement('span'); feChip.className='time-breakdown-chip'; feChip.innerHTML='<span class="chip-label">Render</span>&nbsp;⚡ '+renderMs+'ms'; breakdown.appendChild(feChip);
                    //
                    // let totalLlm=0, totalSql=0;
                    // trace.forEach(s=>{ if(s.llmMs) totalLlm+=s.llmMs; if(s.toolMs) totalSql+=s.toolMs; });
                    // trace.forEach((s,i)=>{
                    //     if(s.llmMs==null&&s.toolMs==null) return;
                    //     const chip=document.createElement('span'); chip.className='time-breakdown-chip';
                    //     chip.innerHTML='<span class="chip-label">Step '+(s.step||i+1)+'</span>'+(s.llmMs!=null?' &nbsp;🤖 '+(s.llmMs/1000).toFixed(2)+'s':'')+(s.toolMs!=null?' &nbsp;🗄️ '+(s.toolMs/1000).toFixed(2)+'s':'');
                    //     breakdown.appendChild(chip);
                    // });
                    // if (trace.filter(s=>s.llmMs!=null||s.toolMs!=null).length>1) {
                    //     const sum=document.createElement('span'); sum.className='time-breakdown-chip'; sum.style.fontWeight='600';
                    //     const parts=[]; if(totalLlm) parts.push('🤖 '+(totalLlm/1000).toFixed(2)+'s'); if(totalSql) parts.push('🗄️ '+(totalSql/1000).toFixed(2)+'s'); parts.push('⚡ '+renderMs+'ms');
                    //     sum.innerHTML='<span class="chip-label">Σ</span>&nbsp;'+parts.join(' &nbsp;·&nbsp; '); breakdown.appendChild(sum);
                    // }
                    // botMsg.content.after(breakdown);
                });
            }
        } catch (error) {
            const totalMs=Date.now()-startTime;
            setBotContent(botMsg, 'Error: '+((error && error.message) || 'Request failed'), true);
            setStatus(false,'Offline');
            const tb=document.createElement('span'); tb.className='time-badge'; tb.innerHTML='&#9201; '+(totalMs/1000).toFixed(2)+'s'; botMsg.meta.prepend(tb);
        } finally {
            isSending=false; sendBtn.disabled=false; userInput.focus();
        }
    }

    function resetConversation() {
        chatWindow.innerHTML='';
        chatWindow.appendChild(emptyState);
        emptyState.style.display='flex';
        latestExportPayload=null;
        if(exportBtn) exportBtn.disabled=true;
        sessionId=newSessionId();
        userInput.value=''; userInput.focus();
    }

    // Prompt chips + sidebar items
    document.querySelectorAll('.suggestion-chip[data-prompt]').forEach(el => {
        el.addEventListener('click', ()=>{
            const p=el.getAttribute('data-prompt');
            if(p){ userInput.value=p; userInput.focus(); sendMessage(); }
        });
    });

    sendBtn.addEventListener('click', sendMessage);
    if(newChatBtn) newChatBtn.addEventListener('click', resetConversation);
    exportBtn.addEventListener('click', exportLatestAnswer);
    closeTraceBtn.addEventListener('click', closeTrace);
    traceModal.addEventListener('click', e=>{ if(e.target===traceModal) closeTrace(); });
    userInput.addEventListener('keydown', e=>{ if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); sendMessage(); } });

    // ── Voice recording ───────────────────────────────────────────────
    const micBtn=document.getElementById('mic-btn'), composerRow=document.getElementById('composer-row');
    const voiceBar=document.getElementById('voice-bar'), voiceCanvas=document.getElementById('voice-canvas');
    const voiceCancelBtn=document.getElementById('voice-cancel-btn'), voiceConfirmBtn=document.getElementById('voice-confirm-btn');

    let mediaRecorder=null, recordingChunks=[], recordingTimer=null, activeStream=null, activeMimeType=null, micState='idle';
    let audioCtx=null, analyser=null, waveRafId=null;

    function setMicState(state) {
        micState=state;
        if (state==='idle') { voiceBar.style.display='none'; composerRow.style.display=''; voiceBar.classList.remove('processing'); voiceCancelBtn.disabled=false; voiceConfirmBtn.disabled=false; }
        else if (state==='recording') { composerRow.style.display='none'; voiceBar.style.display='flex'; voiceBar.classList.remove('processing'); voiceCancelBtn.disabled=false; voiceConfirmBtn.disabled=false; }
        else if (state==='processing') { voiceBar.classList.add('processing'); voiceCancelBtn.disabled=true; voiceConfirmBtn.disabled=true; }
    }

    function startWaveform() {
        const ctx=voiceCanvas.getContext('2d'), DPR=window.devicePixelRatio||1;
        function draw() {
            if(micState!=='recording') return;
            const W=voiceCanvas.offsetWidth, H=voiceCanvas.offsetHeight;
            if(voiceCanvas.width!==W*DPR||voiceCanvas.height!==H*DPR){ voiceCanvas.width=W*DPR; voiceCanvas.height=H*DPR; ctx.scale(DPR,DPR); }
            ctx.clearRect(0,0,W,H);
            if(analyser){ const bins=analyser.frequencyBinCount, freqData=new Uint8Array(bins); analyser.getByteFrequencyData(freqData); const barW=3,gap=2.5,total=barW+gap,count=Math.floor(W/total),step=Math.max(1,Math.floor(bins/count)),centerY=H/2; ctx.fillStyle='#94a3b8'; for(let i=0;i<count;i++){const val=freqData[i*step]/255,half=Math.max(2,val*(H/2)*.9),x=i*total,r=Math.min(barW/2,half); ctx.beginPath(); ctx.roundRect(x,centerY-half,barW,half*2,r); ctx.fill();} }
            else { const count=Math.floor(voiceCanvas.offsetWidth/5.5); ctx.fillStyle='#cbd5e1'; for(let i=0;i<count;i++){const h=4+Math.random()*4; ctx.fillRect(i*5.5,(H-h)/2,3,h);} }
            waveRafId=requestAnimationFrame(draw);
        }
        waveRafId=requestAnimationFrame(draw);
    }

    function stopWaveform() { if(waveRafId){cancelAnimationFrame(waveRafId);waveRafId=null;} voiceCanvas.getContext('2d').clearRect(0,0,voiceCanvas.width,voiceCanvas.height); }
    function startAudioAnalysis(stream) { audioCtx=new(window.AudioContext||window.webkitAudioContext)(); const src=audioCtx.createMediaStreamSource(stream); analyser=audioCtx.createAnalyser(); analyser.fftSize=256; src.connect(analyser); }
    function stopAudioAnalysis() { if(audioCtx){audioCtx.close().catch(()=>{});audioCtx=null;} analyser=null; }

    async function startRecording() {
        try { activeStream=await navigator.mediaDevices.getUserMedia({audio:true}); }
        catch { alert('Microphone access denied.'); return; }
        activeMimeType=MediaRecorder.isTypeSupported('audio/webm;codecs=opus')?'audio/webm;codecs=opus':'audio/webm';
        mediaRecorder=new MediaRecorder(activeStream,{mimeType:activeMimeType}); recordingChunks=[];
        mediaRecorder.ondataavailable=e=>{if(e.data.size>0)recordingChunks.push(e.data);};
        mediaRecorder.onstop=async e=>{ stopAudioAnalysis(); stopWaveform(); activeStream.getTracks().forEach(t=>t.stop()); clearTimeout(recordingTimer); if(e.target._cancelled){setMicState('idle');return;} await transcribeAudio(new Blob(recordingChunks,{type:activeMimeType}),activeMimeType); };
        mediaRecorder.start(100); setMicState('recording'); startAudioAnalysis(activeStream); startWaveform();
        recordingTimer=setTimeout(()=>stopRecording(),60000);
    }

    function stopRecording() { if(mediaRecorder&&mediaRecorder.state!=='inactive'){mediaRecorder._cancelled=false;mediaRecorder.stop();setMicState('processing');} }
    function cancelRecording() { clearTimeout(recordingTimer); stopAudioAnalysis(); stopWaveform(); if(mediaRecorder&&mediaRecorder.state!=='inactive'){mediaRecorder._cancelled=true;mediaRecorder.stop();} if(activeStream){activeStream.getTracks().forEach(t=>t.stop());activeStream=null;} setMicState('idle'); }

    function blobToBase64(blob) { return new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(',')[1]);r.onerror=rej;r.readAsDataURL(blob);}); }

    async function transcribeAudio(blob, mimeType) {
        try {
            const base64=await blobToBase64(blob);
            // Session and tenant go with the audio so the server can transcribe in the language this
            // session is already speaking — Whisper's own detection misreads short non-English phrases.
            const response=await fetch(API_BASE + '/api/transcribe',{method:'POST',headers:{'Content-Type':'application/json','X-CSRF-TOKEN':CSRF_TOKEN},body:JSON.stringify({audio:base64,mimeType,sessionId,tenantDb:getStoredTenantDb()||undefined,userId:getCurrentUserId()||undefined})});
            if(!response.ok) throw new Error('Transcription failed');
            const data=await response.json();
            if(data.text){ userInput.value=data.text; userInput.focus(); }
        } catch(err) { console.error('Transcription error:',err); alert('Transcription failed. Please try again.'); }
        finally { setMicState('idle'); }
    }

    micBtn.addEventListener('click', ()=>{if(micState==='idle') startRecording();});
    voiceCancelBtn.addEventListener('click', cancelRecording);
    voiceConfirmBtn.addEventListener('click', stopRecording);

    // ── Usage dashboard ───────────────────────────────────────────────
    const usageModal=document.getElementById('usage-modal'), usageBtn=document.getElementById('usage-btn');
    const closeUsageBtn=document.getElementById('close-usage'), loadUsageBtn=document.getElementById('load-usage-btn');
    const usageTenantEl=document.getElementById('usage-tenant'), usageFromEl=document.getElementById('usage-from'), usageToEl=document.getElementById('usage-to');
    const summaryEl=document.getElementById('usage-summary'), breakdownEl=document.getElementById('usage-breakdown'), topusersEl=document.getElementById('usage-topusers');

    function todayLocal() { const d=new Date(); return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); }

    function openUsage() {
        usageModal.style.display='flex'; usageModal.setAttribute('aria-hidden','false');
        const t=getStoredTenantDb(); if(t&&usageTenantEl) usageTenantEl.value=t;
        const today=todayLocal(); if(usageFromEl&&!usageFromEl.value) usageFromEl.value=today; if(usageToEl&&!usageToEl.value) usageToEl.value=today;
        loadUsage();
    }

    function closeUsage() { usageModal.style.display='none'; usageModal.setAttribute('aria-hidden','true'); }
    function fmt(n)     { return Number(n).toLocaleString(undefined,{maximumFractionDigits:2}); }
    function fmtCost(n) { return '$'+Number(n).toFixed(6); }
    function buildQs(from,to) { const p=new URLSearchParams(); if(from)p.set('from',from); if(to)p.set('to',to); return p.toString()?'?'+p.toString():''; }

    function renderSummary(data) { summaryEl.innerHTML='<div class="fl-stat-cards"><div class="fl-stat-card"><div class="stat-val">'+fmt(data.totalCalls)+'</div><div class="stat-lbl">Total Calls</div></div><div class="fl-stat-card"><div class="stat-val">'+fmt(data.totalTokensIn)+'</div><div class="stat-lbl">Tokens In</div></div><div class="fl-stat-card"><div class="stat-val">'+fmt(data.totalTokensOut)+'</div><div class="stat-lbl">Tokens Out</div></div><div class="fl-stat-card"><div class="stat-val">'+fmtCost(data.totalCostUsd)+'</div><div class="stat-lbl">Cost (USD)</div></div></div>'; }

    function renderBreakdown(data) {
        if(!data.breakdown || !data.breakdown.length){breakdownEl.innerHTML='<div class="fl-usage-empty">No data yet.</div>';return;}
        breakdownEl.innerHTML='<table class="fl-usage-table"><thead><tr><th>Feature</th><th>Calls</th><th>Tokens In</th><th>Tokens Out</th><th>Cost</th><th>% Cost</th></tr></thead><tbody>'+data.breakdown.map(r=>'<tr><td>'+r.featureTag+'</td><td>'+fmt(r.calls)+'</td><td>'+fmt(r.tokensIn)+'</td><td>'+fmt(r.tokensOut)+'</td><td>'+fmtCost(r.costUsd)+'</td><td><div class="fl-bar-wrap"><div class="fl-bar-bg"><div class="fl-bar-fill" style="width:'+r.percentageOfCost+'%"></div></div><span>'+r.percentageOfCost+'%</span></div></td></tr>').join('')+'</tbody></table>';
    }

    function renderTopUsers(data) {
        if(!data.users || !data.users.length){topusersEl.innerHTML='<div class="fl-usage-empty">No data yet.</div>';return;}
        topusersEl.innerHTML='<table class="fl-usage-table"><thead><tr><th>User</th><th>Tenant</th><th>Calls</th><th>Tokens In</th><th>Tokens Out</th><th>Cost</th></tr></thead><tbody>'+data.users.map(u=>'<tr><td>'+u.userId+'</td><td>'+u.tenantId+'</td><td>'+fmt(u.calls)+'</td><td>'+fmt(u.tokensIn)+'</td><td>'+fmt(u.tokensOut)+'</td><td>'+fmtCost(u.costUsd)+'</td></tr>').join('')+'</tbody></table>';
    }

    async function loadUsage() {
        const tenantId=(usageTenantEl.value||'default').trim(), from=usageFromEl.value.trim(), to=usageToEl.value.trim(), qs=buildQs(from,to);
        loadUsageBtn.disabled=true; summaryEl.innerHTML=breakdownEl.innerHTML=topusersEl.innerHTML='<div class="fl-usage-empty">Loading...</div>';
        try {
            const [sRes,bRes,tRes]=await Promise.all([fetch(API_BASE+'/usage/tenant/'+encodeURIComponent(tenantId)+qs),fetch(API_BASE+'/usage/breakdown/'+encodeURIComponent(tenantId)+qs),fetch(API_BASE+'/usage/top-users'+qs+(qs?'&':'?')+'tenant='+encodeURIComponent(tenantId))]);
            const [sData,bData,tData]=await Promise.all([sRes.json(),bRes.json(),tRes.json()]);
            renderSummary(sData); renderBreakdown(bData); renderTopUsers(tData);
        } catch(err) { const m='<div class="fl-usage-empty">Error: '+((err && err.message)||'Request failed')+'</div>'; summaryEl.innerHTML=breakdownEl.innerHTML=topusersEl.innerHTML=m; }
        finally { loadUsageBtn.disabled=false; }
    }

    usageBtn.addEventListener('click', openUsage);
    closeUsageBtn.addEventListener('click', closeUsage);
    loadUsageBtn.addEventListener('click', loadUsage);
    usageModal.addEventListener('click', e=>{ if(e.target===usageModal) closeUsage(); });

    // ── Model select persistence ────────────────────────────────────────
    const modelSelectEl = document.getElementById('model-select');
    if (modelSelectEl) {
        const savedModel = localStorage.getItem('fl_ai_model');
        if (savedModel) modelSelectEl.value = savedModel;
        modelSelectEl.addEventListener('change', () => { localStorage.setItem('fl_ai_model', modelSelectEl.value); });
    }

    // ── Init ──────────────────────────────────────────────────────────
    checkHealth();
    userInput.focus();
    setInterval(checkHealth, 30000);
    } // end initFLChat

    window.initFLChat = initFLChat;

    // Auto-init when the chat markup is already present at page load (the /chat page).
    if (document.getElementById('chat-window')) {
        initFLChat();
    }
})();
